// Cell editing utilities for PdfTableViewer

/**
 * Scroll a table cell into view
 */
export const scrollCellIntoView = (rowIndex, colIndex) => {
  // Find the cell element by data attributes
  const cellSelector = `[data-row="${rowIndex}"][data-col="${colIndex}"]`;
  const cellElement = document.querySelector(cellSelector);

  if (cellElement) {
    cellElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
  }
};

/**
 * Handle clicking on a table cell
 */
export const handleCellClick = (
  rowIndex,
  colIndex,
  {
    editingCell,
    cancelCellEdit,
    tableData,
    selectedTable,
    setSelectedCell,
    setSelectedWordIndex,
    currentPage,
    setCurrentPage,
    highlightWordsOnPdf,
    shouldScrollIntoView = false,
  }
) => {
  // Cancel any ongoing edit when clicking a different cell
  if (
    editingCell &&
    (editingCell.row !== rowIndex || editingCell.col !== colIndex)
  ) {
    cancelCellEdit();
  }

  const cellKey = `${rowIndex}_${colIndex}`;

  // Get the current text from tableData (which may have been edited)
  let currentText = "";
  if (tableData) {
    if (rowIndex === 0) {
      currentText = tableData.headers[colIndex] || "";
    } else {
      currentText = tableData.rows[rowIndex - 1]?.[colIndex] || "";
    }
  }

  // Extract words from the cell location data
  let words = [];
  let page = 1;

  // The selectedTable contains cell location data directly as properties like "0_0", "0_1", etc.
  const cellLocationData = selectedTable?.[cellKey];

  if (cellLocationData && Array.isArray(cellLocationData)) {
    // cellLocationData is an array of cell info objects
    const firstCellInfo = cellLocationData[0];
    if (firstCellInfo) {
      words = firstCellInfo.words || [];
      page = firstCellInfo.page || 1;
    }
  }

  const cellInfo = {
    row: rowIndex,
    col: colIndex,
    text: currentText,
    locationData: cellLocationData,
    page: page,
    words: words,
  };

  // Debug: console.log('Cell clicked:', cellKey, 'Cell info:', cellInfo);

  setSelectedCell(cellInfo);
  setSelectedWordIndex(null); // Reset word selection

  // Scroll the cell into view in the table (only when navigating from PDF)
  if (shouldScrollIntoView) {
    setTimeout(() => {
      scrollCellIntoView(rowIndex, colIndex);
    }, 100);
  }

  // Navigate to page if different
  if (cellInfo.page !== currentPage) {
    setCurrentPage(cellInfo.page);
  } else {
    highlightWordsOnPdf(cellInfo);
  }
};

/**
 * Handle clicking on a specific word within a cell
 */
export const handleWordClick = (
  wordIndex,
  {
    setSelectedWordIndex,
    selectedCell,
    currentPage,
    setCurrentPage,
    highlightWordsOnPdf,
  }
) => {
  setSelectedWordIndex(wordIndex);
  if (selectedCell) {
    // Navigate to page if different
    if (selectedCell.page !== currentPage) {
      setCurrentPage(selectedCell.page);
    } else {
      highlightWordsOnPdf(selectedCell, wordIndex);
    }
  }
};

/**
 * Start editing a cell
 */
export const startCellEdit = (
  selectedCell,
  { setEditingCell, setEditValue }
) => {
  if (selectedCell) {
    setEditingCell({ row: selectedCell.row, col: selectedCell.col });
    setEditValue(selectedCell.text);
  }
};

/**
 * Save the current cell edit
 */
export const saveCellEdit = ({
  editingCell,
  selectedTable,
  tableData,
  editValue,
  setTableData,
  setSelectedCell,
  setEditingCell,
  setEditValue,
}) => {
  if (editingCell && selectedTable && tableData) {
    const updatedData = { ...tableData };

    if (editingCell.row === 0) {
      // Editing header
      updatedData.headers[editingCell.col] = editValue;
    } else {
      // Editing data row (row - 1 because headers are row 0)
      if (!updatedData.rows[editingCell.row - 1]) {
        updatedData.rows[editingCell.row - 1] = [];
      }
      updatedData.rows[editingCell.row - 1][editingCell.col] = editValue;
    }

    setTableData(updatedData);

    // Update the selected cell text
    setSelectedCell((prev) => (prev ? { ...prev, text: editValue } : null));

    // Clear editing state
    setEditingCell(null);
    setEditValue("");
  }
};

/**
 * Cancel the current cell edit
 */
export const cancelCellEdit = ({ setEditingCell, setEditValue }) => {
  setEditingCell(null);
  setEditValue("");
};

/**
 * Handle clicking on the PDF to navigate to the closest cell
 */
export const handlePdfClick = (event, params) => {
  const {
    canvasRef,
    highlightContainerRef,
    pdfPageDimensions,
    selectedTable,
    tableData,
    currentPage,
    getCanvasCoordinates,
    convertCanvasToPdfCoordinates,
    findCellAtPdfCoordinates,
  } = params;
  // Get canvas coordinates from the click event
  const canvasCoords = getCanvasCoordinates(
    event,
    canvasRef,
    highlightContainerRef
  );
  if (!canvasCoords) return false;

  // Convert canvas coordinates to PDF coordinates
  const pdfCoords = convertCanvasToPdfCoordinates(
    canvasCoords.x,
    canvasCoords.y,
    canvasRef,
    pdfPageDimensions
  );
  if (!pdfCoords) return false;

  // Find the cell containing the click location
  const foundCell = findCellAtPdfCoordinates(
    pdfCoords.x,
    pdfCoords.y,
    selectedTable,
    tableData,
    currentPage
  );

  if (foundCell) {
    // Navigate to the found cell using the utility function directly (not the wrapped version from hook)
    handleCellClick(foundCell.rowIndex, foundCell.colIndex, {
      editingCell: params.editingCell,
      cancelCellEdit: params.cancelCellEdit,
      tableData: params.tableData,
      selectedTable: params.selectedTable,
      setSelectedCell: params.setSelectedCell,
      setSelectedWordIndex: params.setSelectedWordIndex,
      currentPage: params.currentPage,
      setCurrentPage: params.setCurrentPage,
      highlightWordsOnPdf: params.highlightWordsOnPdf,
      shouldScrollIntoView: true, // enable scrolling for PDF-to-cell navigation
    });
    return true; // Indicate that we handled the click
  }
  return false; // No cell found, let other handlers process the click
};
